// Display name mapping for known business role names
const ROLE_DISPLAY_NAMES = {
  pz_biz_admin: 'Admin',
  pz_biz_genuser_plus: 'General Plus',
  pz_biz_genuser: 'General',
  pz_biz_view_plus: 'View Only Plus',
  pz_biz_view: 'View Only',
  pz_biz_no_access: 'No Access',
};

// Constant for unassigned role display
const UNASSIGNED_ROLE_LABEL = 'Not Assigned';

/**
 * Formats a raw business role name for display in the UI.
 * Falls back to dynamic transformation for unmapped roles.
 * @param {string} roleName - The raw role name from API (e.g., "pz_biz_genuser_plus")
 * @returns {string} Formatted display name (e.g., "General Plus")
 */
export const formatRoleName = (roleName) => {
  if (!roleName) return '';

  const lowerRoleName = roleName.toLowerCase();

  if (ROLE_DISPLAY_NAMES[lowerRoleName]) {
    return ROLE_DISPLAY_NAMES[lowerRoleName];
  }

  // Fallback: strip prefix, replace underscores, capitalize each word
  return lowerRoleName
    .replace(/^pz_biz_/, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

/**
 * Extracts and formats the role from the nested user object.
 * bizRoles lives at user.bizRoles in the API response (payerUser.user.bizRoles).
 *
 * Rules:
 * - bizRoles present with a valid bizRoleName → return formatted name
 * - bizRoles present but empty ([]) or missing bizRoleName → return UNASSIGNED_ROLE_LABEL
 * - bizRoles absent entirely (undefined) → fall back to fallbackRole
 *
 * @param {Object} user - The nested user object (payerUser.user from API)
 * @param {string} fallbackRole - The payerUser-level role field as fallback
 * @returns {string} Formatted role name, UNASSIGNED_ROLE_LABEL, or fallback role value
 */
export const getUserRoleName = (user, fallbackRole = '') => {
  // If bizRoles property exists on user, use bizRoles path exclusively
  if (user && Array.isArray(user.bizRoles)) {
    const bizRoleName = user.bizRoles[0]?.bizRoleName;
    return bizRoleName ? formatRoleName(bizRoleName) : UNASSIGNED_ROLE_LABEL;
  }

  // bizRoles absent entirely — fall back to legacy role field
  return fallbackRole || '';
};
